import { type SyntheticEvent, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';

import type { Idea } from '../../interfaces/Idea';
import CoreQuillEditor from '../shared/CoreQuillEditor';
import { resolveIdeaImageSrc } from '../../utils/ideaImage';

interface IdeaCardEditorProps {
  idea: Idea;
  editorValue: string;
  isSavingEdit: boolean;
  canSaveEdit: boolean;
  imageRotation: number;
  onEditorValueChange: (value: string) => void;
  onRotateImage: (offset: number) => void;
  onCancelEditing: () => void;
  onSaveEditing: () => void | Promise<void>;
  onCardClick: () => void;
}

const IdeaCardEditor = ({
  idea,
  editorValue,
  isSavingEdit,
  canSaveEdit,
  imageRotation,
  onEditorValueChange,
  onRotateImage,
  onCancelEditing,
  onSaveEditing,
  onCardClick,
}: IdeaCardEditorProps) => {
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const normalizedRotation = ((imageRotation % 360) + 360) % 360;
  const isSideways = normalizedRotation === 90 || normalizedRotation === 270;
  const imageAspectRatio =
    imageSize && imageSize.height > 0 ? imageSize.width / imageSize.height : null;
  const previewAspectRatio = imageAspectRatio
    ? isSideways
      ? 1 / imageAspectRatio
      : imageAspectRatio
    : undefined;

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    setImageSize({ width: image.naturalWidth, height: image.naturalHeight });
  };

  return (
    <Card
      id={`idea-body_${idea.id}`}
      elevation={4}
      sx={{ position: 'relative', cursor: 'default' }}
      onClick={onCardClick}
    >
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        {idea.type === 'image' && (
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
              borderRadius: 1,
              overflow: 'hidden',
              mb: 1,
              position: 'relative',
              ...(isSideways && previewAspectRatio ? { aspectRatio: `${previewAspectRatio}` } : {}),
            }}
          >
            <CardMedia
              component="img"
              image={resolveIdeaImageSrc(idea)}
              onLoad={handleImageLoad}
              sx={{
                display: 'block',
                ...(isSideways && imageAspectRatio
                  ? {
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: `${imageAspectRatio * 100}%`,
                      maxWidth: 'none',
                      transform: `translate(-50%, -50%) rotate(${normalizedRotation}deg)`,
                    }
                  : {
                      width: '100%',
                      transform: `rotate(${normalizedRotation}deg)`,
                    }),
              }}
            />
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                position: 'absolute',
                right: 8,
                bottom: 8,
                bgcolor: 'rgba(255, 255, 255, 0.86)',
                borderRadius: 1,
              }}
            >
              <Tooltip title="Rotate left">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onRotateImage(-90)}
                    disabled={isSavingEdit}
                    aria-label="Rotate image left"
                  >
                    <RotateLeftIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Rotate right">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onRotateImage(90)}
                    disabled={isSavingEdit}
                    aria-label="Rotate image right"
                  >
                    <RotateRightIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        )}
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            '.ql-container': {
              border: 0,
            },
            '.ql-editor': {
              minHeight: 120,
            },
          }}
        >
          <CoreQuillEditor value={editorValue} onChange={onEditorValueChange} />
        </Box>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          mt={1.5}
          onClick={(event) => event.stopPropagation()}
        >
          <Button size="small" onClick={onCancelEditing} disabled={isSavingEdit}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => void onSaveEditing()}
            disabled={!canSaveEdit || isSavingEdit}
          >
            Save
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default IdeaCardEditor;
